import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  communicationApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const recipientListApi = {
  getAllRecipientList: async (
    utilityId: string,
    page: number,
    limit: number,
    search?: string,
    audienceType?: number,
    listType?: number,
    status?: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "recipient-lists",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("page", page);
        query.push("limit", limit);
        if (search) {
          query.push("search_data", search);
        }
        if (audienceType) {
          query.push("audience_type", audienceType);
        }
        if (listType) {
          query.push("list_type", listType);
        }
        if (status) {
          query.push("status", status);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getRecipientDetails: async (utilityId: string, listId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `recipient-lists/${listId}/detail`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getRecipientListSummary: async (utilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "recipient-lists/summary",
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  getAllCustomerMemberList: async (
    utilityId: string,
    segmentId: number,
    page: number,
    limit: number,
    search?: string,
    filterType?: string,
    areas?: string[],
    subAreas?: string[],
    category?: string[],
    subCategory?: string[],
    status?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "fetch-consumer-data",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("filter_type", "TERRITORY");
        query.push("active_consumers", 1);
        query.push("page", page);
        query.push("limit", limit);
        query.push("segment_id", segmentId);
        if (search) {
          query.push("search_data", search);
        }
        if (filterType) {
          query.push("filter_type", filterType);
        }
        if (areas) {
          for (const area of areas) {
            query.push("area", area);
          }
        }
        if (subAreas) {
          for (const subArea of subAreas) {
            query.push("sub_area", subArea);
          }
        }
        if (category) {
          for (const cat of category) {
            query.push("category", cat);
          }
        }
        if (subCategory) {
          for (const subCat of subCategory) {
            query.push("sub_category", subCat);
          }
        }
        if (status) {
          query.push("status", status);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getAllSegments: async (utilityId: string, audienceType?: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "segments",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("dropdown_only", true);
        query.push("audience_type", audienceType);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  createRecipientList: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "recipient-lists"
    );
    const response = await communicationApiClient.post(url, payload);
    return response.data || response.data.result;
  },
  updateRecipientList: async (payload: any, id: number): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `recipient-lists/${id}`
    );
    const response = await communicationApiClient.patch(url, payload);
    return response.data || response.data.result;
  },

  getUserInfo: async (params: {
    remoteUtilityId: any;
    segmentId: number;
    filterType?: string;
    page?: number;
    limit?: number;
    searchData?: string;
    departments?: string[];
    roles?: string[];
    status?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-info",
      (qs) => {
        qs.push("remote_utility_id", params.remoteUtilityId.toString());
        qs.push("segment_id", params.segmentId);
        if (params.filterType)
          qs.push("filter_type", params.filterType.toString());
        if (params.page) qs.push("page", params.page.toString());
        if (params.limit) qs.push("limit", params.limit.toString());
        if (params.searchData) qs.push("search_data", params.searchData);
        if (params.departments)
          for (const department of params.departments) {
            qs.push("departments", department);
          }
        if (params.roles)
          for (const role of params.roles) {
            qs.push("roles", role);
          }
        if (params.status) qs.push("status", params.status);
      }
    );

    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getContactInfo: async (utilityId: string, listId: number) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `recipient-lists/contact-info/${listId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  getAllRecipientData: async (
    listId: number,
    utilityId: string,
    page: number,
    limit: number,
    search?: string
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `recipient-lists/recipients-data/${listId}`,
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("enrich_data", true);
        query.push("page", page);
        query.push("limit", limit);
        if (search) {
          query.push("search_data", search);
        }
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },

  getAllRecipientUsageHistory: async (
    listId: number,
    utilityId: string,
    page: number,
    limit: number
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `recipient-lists/${listId}/usage-history`,
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("enrich_data", true);
        query.push("page", page);
        query.push("limit", limit);
      }
    );
    const response = await communicationApiClient.get(url);
    return response.data ?? [];
  },
  getAllDepartments: async (utilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `config-map/department_json`,
      (query) => {
        query.push("remote_utility_id", utilityId);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data ?? [];
  },
  getAllRoles: async (utilityId: string, department: string[]) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      `role-configuration`,
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("department", department);
      }
    );
    const response = await authApiClient.get(url);
    return response.data ?? [];
  },
  createDuplicateRecipientList: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      `recipient-lists/duplicate/${payload.list_id}`
    );
    const response = await communicationApiClient.post(url, payload);
    return response.data || response.data.result;
  },
  getStatus: async (): Promise<any> => {
    const response = await cxApiClient.get("choices/consumer_status");
    return response.data;
  },
  getTerritoryConfigMap: async (
    configLevel: "area" | "sub_area" | "premise" = "area",
    remoteUtilityId: string,
    codeList?: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `config-map/territory_json`,
      (qs: QueryStringParameters) => {
        qs.push("remote_utility_id", remoteUtilityId);
        qs.push("config_level", configLevel);
        if (codeList) {
          for (const code of codeList) {
            qs.push("code_list", code);
          }
        }
      }
    );
    const response = await onboardingApiClient.get(url);

    const data = response.data?.result;

    return data.map((item: any) => ({
      id: item.code,
      name: item.name,
    }));
  },

  getCategoryConfigMap: async (): Promise<any> => {
    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `config-map/category_json`,
      (qs: QueryStringParameters) => {
        qs.push("remote_utility_id", REMOTE_UTILITY_ID);
        qs.push("config_level", "category");
      }
    );
    const response = await onboardingApiClient.get(url);

    const data = response.data?.result;
    return data.map((item: any) => ({
      id: item.code,
      name: item.name,
    }));
  },

  getSubCategoryConfigMap: async (
    codes: string[]
  ): Promise<any> => {
    console.log("subCategory received", codes);

    const REMOTE_UTILITY_ID = getRemoteUtilityId();
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (qs) => {
        qs.push("remote_utility_id", REMOTE_UTILITY_ID);
        qs.push("config_level", "sub_category");
        if (codes) {
          // If filter is an array, push each element
          if (Array.isArray(codes)) {
            if (Array.isArray(codes)) {
              codes.forEach((code) => {
                qs.push("code_list", code);
              });
            }
          }
        }
      }
    );
    const response = await onboardingApiClient.get(url);
    const data = response.data?.result;

    return data.map((item: any) => ({
      id: item.code,
      name: item.name,
    }));
  },
};
